import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICourse } from '../../../models/courses.model';
import Button from '../../Button';
import { Select } from 'antd';
import SelectDateModal from '../../modals/SelectDateModal';
import moment from 'moment';
import { getCommonCodeLabel } from '../../../lib/store';
import Radio from '../../Radio';
import CustomTimePicker from '../../CustomTimePicker';
import useNotification from '../../../hooks/useNotification';

interface IProps {
  courseInfo: ICourse;
  scheduleInput: any;
  onSetScheduleInput: (index: number, key: string, value: string) => void;
  dayList: any;
  onSetStartTime: (i1: number, i2: number, value: string) => void;
  onSetScheduleClassesInput: (i1: number, i2: number, key: string, value: any) => void;
  classroomList: any;
  campus: string | undefined;
  setTeacherInputRef: any;
  onOpenSelectTeacher: () => void;
  regYn: string;
}

const ClassTimetable: FC<IProps> = ({
  courseInfo,
  scheduleInput,
  onSetScheduleInput,
  dayList,
  onSetStartTime,
  onSetScheduleClassesInput,
  classroomList,
  campus,
  setTeacherInputRef,
  onOpenSelectTeacher,
  regYn,
}) => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();
  const [classItem, setClassItem] = useState<any>();
  const [selectDateVisible, setSelectDateVisible] = useState(false);

  const onOpenDateModal = async (classItem: any) => {
    if (!classItem.startTime) {
      return infoNotification(t('class.class.message17'));
    }

    setClassItem(classItem);
    setSelectDateVisible(true);
  }

  // 수업시간 테이블을 그린다
  const renderClassTimeTable = () => {
    if (!courseInfo) {
      return (
        <tr>
          <td className="px-1 v-line text-center" colSpan={5}>
            {t('class.class.text10')}
          </td>
        </tr>
      );
    }

    const weekLessonCount = courseInfo.weekLectureCount;
    const dayLessonCount = courseInfo.dayLessonCount;

    return [...Array(weekLessonCount)].map((n1, i1) => {
      return (
        <Fragment key={i1}>
          <tr>
            <td className="text-center" rowSpan={dayLessonCount}>
              {
                scheduleInput[i1]?.day
                  ? getCommonCodeLabel('SY0001', scheduleInput[i1]?.day, 'ko')
                  : [...Array(5)].map((n2, i2) => {
                    return (
                      <Radio
                        key={i2}
                        checked={scheduleInput[i1]?.day === dayList[i2]?.value}
                        className="ml-2"
                        onChange={() => onSetScheduleInput(i1, 'day', dayList[i2]?.value)}
                        text={dayList[i2]?.label}
                        disabled={regYn === 'Y'}
                      />
                    )
                  })
              }
            </td>
            <td className="text-center">1</td>
            <td className="text-center w-120p">
              <div className='time-picker__area w-120p'>
                <CustomTimePicker
                  value={scheduleInput[i1]?.classes[0].startTime}
                  onSelect={value => {
                    if (value) {
                      onSetStartTime(i1, 0, moment(value).format('HH:mm'));
                    } else {
                      onSetStartTime(i1, 0, '');
                    }
                  }}
                  minuteStep={10}
                />              
              </div>
            </td>
            <td className="text-center">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={String(scheduleInput[i1]?.classes[0].classroom)}
                onChange={value => onSetScheduleClassesInput(i1, 0, 'classroom', value)}
                disabled={!campus}
              >
                {
                  classroomList?.map((item: any) => {
                    return (
                      <Select.Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Select.Option>
                    )
                  })
                }
              </Select>
            </td>
            <td>
              <Button
                className='btn btn-sm btn-success btn-w55p mr-1'
                onClick={() => {
                  setTeacherInputRef({ index1: i1, index2: 0 });
                  onOpenSelectTeacher();
                }}
                text={t('common.button.select')}
              />
              {scheduleInput[i1]?.classes[0].teacher?.memberName}
            </td>
            {
              regYn === 'Y'
                ? (
                  <td>
                    <Button
                      className='btn btn-sm btn-success btn-w55p'
                      onClick={() => onOpenDateModal(scheduleInput[i1].classes[0])}
                      text={t('common.button.save')}
                    />
                  </td>
                )
                : null
            }
          </tr>
          {[...Array((dayLessonCount || 1) - 1)].map((n3, i3) => {
            return (
              <tr key={i3}>
                <td className="text-center">{i3 + 2}</td>
                <td className="text-center">
                  <div className='time-picker__area w-120p'>
                    <CustomTimePicker
                      value={scheduleInput[i1]?.classes[i3 + 1]?.startTime}
                      onSelect={value => {
                        if (value) {
                          onSetStartTime(i1, i3 + 1, moment(value).format('HH:mm'));
                        } else {
                          onSetStartTime(i1, i3 + 1, '');
                        }
                      }}
                      minuteStep={10}
                    />              
                  </div>
                </td>
                <td className="text-center">
                  <Select
                    className="w-100"
                    placeholder={t('common.button.select')}
                    value={String(scheduleInput[i1]?.classes[i3 + 1]?.classroom)}
                    onChange={value => onSetScheduleClassesInput(i1, i3 + 1, 'classroom', value)}
                    disabled={!campus}
                  >
                    {
                      classroomList?.map((item: any) => {
                        return (
                          <Select.Option
                            key={item.value}
                            value={item.value}
                          >
                            {item.label}
                          </Select.Option>
                        )
                      })
                    }
                  </Select>
                </td>
                <td>
                  <Button
                    className='btn btn-sm btn-success btn-w55p mr-1'
                    onClick={() => {
                      setTeacherInputRef({ index1: i1, index2: i3 + 1 });
                      onOpenSelectTeacher();
                    }}
                    text={t('common.button.select')}
                  />
                  {scheduleInput[i1]?.classes[i3 + 1]?.teacher?.memberName}
                </td>
                {
                  regYn === 'Y'
                    ? (
                      <td>
                        <Button
                          className='btn btn-sm btn-success btn-w55p'
                          onClick={() => onOpenDateModal(scheduleInput[i1].classes[i3 + 1])}
                          text={t('common.button.save')}
                        />
                      </td>
                    )
                    : null
                }
              </tr>
            )
          })}
        </Fragment>
      )
    })
  }

  return (
    <>
      <div className="text-left mt-2">
        <h5>{t('class.class.title5')}</h5>
      </div>
      <table className="table-custom">
        <colgroup>
          <col width="*" />
          <col width="50px" />
          <col width="90px" />
          <col width="15%" />
          <col width="25%" />
          {regYn === 'Y' && <col width="10%" />}
        </colgroup>
        <tbody>
          <tr>
            <th className="text-center">{t('class.class.column21')}</th>
            <th className="text-center">{t('class.class.column22')}</th>
            <th className="text-center">{t('class.class.column23')}</th>
            <th className="text-center">{t('class.class.column24')}</th>
            <th className="text-center">{t('class.class.column25')}</th>
            {regYn === 'Y' && <th className="text-center">{t('common.button.save')}</th>}
          </tr>
          {renderClassTimeTable()}
        </tbody>
      </table>

      {selectDateVisible &&
        <SelectDateModal
          visible={selectDateVisible}
          setVisible={setSelectDateVisible}
          classItem={classItem}
        />      
      }
    </>
  );
}

export default ClassTimetable;
